import { ReactNode, useState } from 'react';

import styled from 'styled-components';

import { Collapse, CollapseProps } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Badge } from '@components/badge';
import { IconDropRight } from '@components/icons/dropright';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type ExpandableCardProps = {
  title: string;
  children: ReactNode;
  icon?: ReactNode;
  description?: string;
  counter?: string | number;
  headerAction?: ReactNode;
  defaultClosed?: boolean;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  overflowType?: CollapseProps['overflowType'];
};

export const ExpandableCard = ({
  title,
  children,
  icon,
  description,
  counter,
  headerAction,
  defaultClosed = false,
  isOpen: controlledIsOpen,
  onToggle,
  overflowType = 'visible',
}: ExpandableCardProps) => {
  const isControlled = controlledIsOpen !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(!defaultClosed);
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleToggle = () => {
    const nextIsOpen = !isOpen;
    if (!isControlled) {
      setInternalIsOpen(nextIsOpen);
    }
    onToggle?.(nextIsOpen);
  };

  return (
    <Container>
      <Header
        orientation="horizontal"
        space={2}
        align="center"
        justify="space-between"
        onClick={handleToggle}
      >
        <TitleStack orientation="horizontal" space={2} align="center">
          {icon}
          <TitleBlock>
            <Text text={title} font="semibold14" ellipsisWithTooltip />
            {description && (
              <Text text={description} font="regular12" color="textSecondary" ellipsisWithTooltip />
            )}
          </TitleBlock>
        </TitleStack>
        <Stack orientation="horizontal" space={2} align="center">
          {counter !== undefined && <Badge size="md" text={counter} />}
          {headerAction && (
            <HeaderActionSlot onClick={(event) => event.stopPropagation()}>
              {headerAction}
            </HeaderActionSlot>
          )}
          <Rotate isRotated={isOpen}>
            <IconDropRight color="buttonIconPositive" />
          </Rotate>
        </Stack>
      </Header>
      <Collapse isCollapsed={!isOpen} keepCollapsedInDom overflowType={overflowType}>
        <Content>{children}</Content>
      </Collapse>
    </Container>
  );
};

const Container = styled.div({
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  borderRadius: pxToRem(8),
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
});

const Header = styled(Stack)({
  cursor: 'pointer',
});

const TitleStack = styled(Stack)({
  minWidth: 0,
  flex: 1,
});

const TitleBlock = styled.div({
  minWidth: 0,
});

const HeaderActionSlot = styled.div({
  display: 'flex',
});

const Content = styled.div({
  marginTop: pxToRem(8),
});
